import { useState, type FormEvent } from "react";
import type { ParticipationPromptPublic } from "@tih/shared";
import { ApiError, postParticipationOpenText } from "../../api/client";
import { useBotProtection } from "../../hooks/useBotProtection";
import { useSiteCopy } from "../../context/SiteCopyContext";
import { getAtPath } from "../../lib/siteCopyPaths";
import { HoneypotField } from "../forms/HoneypotField";
import { TurnstileWidget } from "../forms/TurnstileWidget";

type Props = {
  prompt: ParticipationPromptPublic;
  onSuccess: () => void;
};

export function ParticipationOpenTextForm({ prompt, onSuccess }: Props) {
  const { copy } = useSiteCopy();
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const maxLen = prompt.maxLength || 280;
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ready || busy) return;
    setError(null);
    setBusy(true);
    try {
      await postParticipationOpenText({
        promptSlug: prompt.slug,
        body: body.trim(),
        name: name.trim() || undefined,
        ...botFields,
      });
      setDone(true);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not save your answer");
      resetBotProtection();
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="form-success">
        <strong>{String(getAtPath(copy, "home.participation.submitThanks") ?? "")}</strong>
        <p style={{ marginTop: 8, opacity: 0.85 }}>
          {String(getAtPath(copy, "home.participation.pendingReview") ?? "")}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="form-stack">
      <HoneypotField value={website} onChange={setWebsite} />
      <label>
        <span>Your name (optional)</span>
        <input
          type="text"
          maxLength={120}
          placeholder="Anonymous is fine"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
        />
      </label>
      <label>
        <span>Your answer</span>
        <textarea
          required
          minLength={1}
          maxLength={maxLen}
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={busy}
          placeholder={prompt.placeholder ?? ""}
        />
        <span className="form-help">
          {body.length} / {maxLen}
        </span>
      </label>
      {needsTurnstile && config?.turnstileSiteKey && (
        <TurnstileWidget
          siteKey={config.turnstileSiteKey}
          onToken={onTurnstileToken}
          onExpire={onTurnstileExpire}
          onRegisterReset={registerTurnstileReset}
        />
      )}
      {error && <div className="form-error">{error}</div>}
      <button type="submit" className="btn btn-primary" disabled={busy || !ready}>
        {busy ? "Sending…" : "Share"}
      </button>
    </form>
  );
}
